// React
import React, {
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

//refine
import { useTranslation } from '@refinedev/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Mantine
import { Flex, useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { QueryClient } from '@tanstack/react-query';
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_Row,
  MRT_SortingState,
  MRT_TableInstance,
} from 'mantine-react-table';

// Interfaces
import {
  Claim,
  CreateClaimForm,
  EOB,
  Payor,
  Provider,
  Remittance,
  SharedClaimForm,
  Site,
  Status,
} from 'interfaces';
import { parseDateTime } from 'utils/dateUtils';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';

// Column
import { ClaimColumns } from 'columns/Claim';
import { Global } from '@emotion/react';
import {
  BILLER_CLAIM_COLUMN_ORDER,
  BILLER_CLAIMS_CLASS_NAMES,
  getBillerClaimsStyles,
} from 'styles/BillerClaimsStyles';

// Components
import { BillingDataGridWrapper } from 'components/common';
import {
  ClaimToolBar,
  useClaimHandlers,
  ClaimBulkResubmitModal,
  ClaimBulkReconcileModal,
  ClaimBulkCancelModal,
  BulkClaimEditPayload,
  getClaimTableOptions,
  ClaimRowModals,
  bulkReconcileTariffs,
  ClaimsContextMenu,
  ClaimsToolbarInternalActions,
  getDefaultColumnVisibility,
  getDefaultVisibleColumns,
  getProviderColumnOrder,
  enhanceColumns,
  ClaimTotalsPanel,
  useClaimTariffActions,
  useClaimRowModalState,
  useClaimTableState,
  useClaimBulkState,
  useClaimTableData,
} from './components';

// API
import { CountReturnTypes } from 'api/utils/queries';

//Hooks
import {
  useClaimPaginationStore,
  useSelectedClaimStore,
  useSettingsStore,
  useCurrentSiteStore,
  useCurrentUserStore,
} from 'store';
import { useClaimTabFilterStore } from 'store/ClaimTabFilterStore';
import { useClaimEditStepperStore } from 'store/ClaimEditStepperStore';
import {
  useBulkCancelClaims,
  useBulkEditClaims,
  useBulkReconcileTariffs,
} from 'api/resource';
import {
  getClaimColumnFiltersFromSearchParams,
  hasAllClaimColumnFilters,
  mergeClaimColumnFilters,
} from './utils/claimFiltersFromQuery';
import {
  buildClaimTableRows,
  buildClaimRowSelection,
  buildClaimRowId,
  buildTariffRowId,
  ClaimTableRow,
  getParentClaim,
  getClaimTariffRowDebits,
  getClaimIdsFromRowSelection,
  getTariffSelectionFromRowSelection,
  isClaimParentRow,
} from './utils/claimTableRows';
import { applyClaimTariffRowFilters } from './utils/claimTariffRowFilters';
import { ProviderNeedsYou } from './components/ProviderClaimFilters';
import { PROVIDER_CLAIMS_ACCENT_COLOR } from './components/ProviderClaimsStyles';
import {
  CLAIMS_WINDOW_CHANNEL,
  CLAIMS_WINDOW_TARGET,
  getQuickCreateTypeFromSearchParams,
  QUICK_CREATE_PARAM_KEYS,
} from 'utils/crossTabNavigation';

import { useCanModify, useIsAdmin, useIsProvider } from 'hooks';
import useInvalidRowHighlighting from 'hooks/useInvalidRowHighlighting';
import {
  areTariffStatusesEligible,
  claimHasTariffStatus,
  isClaimTariffStatusValue,
  isReconcileEligibleTariffStatus,
  isResubmittableTariffStatus,
} from 'utils/claimStatusUtils';

interface ClaimManagementProps {
  claims: {
    data: Claim[];
    total: number;
    nextPage: number;
    totalAmountSubmitted: number;
    totalAmountPaid: number;
    totalAmountOutstanding: number;
    payorTariffCounts?: Record<string, number>;
  };
  claimStatuses: CountReturnTypes;
  providers: Provider[];
  sites: Site[];
  status: Status[];
  payors: Payor[];
  remittances: Remittance[];
  eobs: EOB[];
  eobCounts: CountReturnTypes;
  providerActionRequestCount?: number;
  actionNeededCount?: number;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  setPagination: Dispatch<
    SetStateAction<{
      pageIndex: number;
      pageSize: number;
      globalFilter: string | undefined;
      columnFilters: MRT_ColumnFiltersState;
      sorting: MRT_SortingState;
    }>
  >;
  pagination: {
    pageIndex: number;
    pageSize: number;
    globalFilter: string | undefined;
    columnFilters: MRT_ColumnFiltersState;
    sorting: MRT_SortingState;
  };
  invalidData: string[];
  errorMessage: string;
  refetchClaims: () => void;
  refetchStatusesCount: () => void;
  loadEobLookups?: () => void;
  queryClient?: QueryClient;
}

const clearQuickCreateSearchParams = (params: URLSearchParams) => {
  const cleanedParams = new URLSearchParams(params);
  cleanedParams.delete(QUICK_CREATE_PARAM_KEYS.createType);
  cleanedParams.delete(QUICK_CREATE_PARAM_KEYS.providerID);
  cleanedParams.delete(QUICK_CREATE_PARAM_KEYS.intakeItemID);
  cleanedParams.delete(QUICK_CREATE_PARAM_KEYS.siteID);
  cleanedParams.delete(QUICK_CREATE_PARAM_KEYS.serviceDate);
  return cleanedParams;
};

export const ClaimManagement: React.FC<ClaimManagementProps> = ({
  claims,
  claimStatuses,
  providers,
  sites,
  status,
  payors,
  remittances,
  eobs,
  eobCounts,
  providerActionRequestCount = 0,
  actionNeededCount = 0,
  isLoading,
  isError,
  isFetching,
  invalidData,
  errorMessage,
  refetchClaims,
  refetchStatusesCount,
  loadEobLookups,
  queryClient,
}) => {
  // hooks
  useDocumentTitle('Claims | QBilling');
  const { translate } = useTranslation();
  const { id: editingId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = useComputedColorScheme('dark');
  const theme = useMantineTheme();
  const isProvider = useIsProvider();
  const isAdmin = useIsAdmin();
  const { canModify: canModifyClaims } = useCanModify('claims');
  const isSmallScreen = useMediaQuery('(max-width: 1000px)');
  const { mantineTableBodyRowProps } = useInvalidRowHighlighting({
    invalidData,
  });

  const { pagination, setPagination } = useClaimPaginationStore();
  const {
    id: currentUserID,
    role: currentUserRole,
    providerID: currentProviderID,
    firstName: currentUserFirstName,
    lastName: currentUserLastName,
  } = useCurrentUserStore();
  const site = useCurrentSiteStore();
  const isAllSites = site.providerSiteSelection === 'all';
  const bulkReconcileTariffsMutation = useBulkReconcileTariffs();
  const bulkCancelClaimsMutation = useBulkCancelClaims();
  const bulkEditClaimsMutation = useBulkEditClaims();
  const {
    providerClaimColumnLayoutVersion,
    applyProviderClaimColumnDefaults,
    persistedVisibleColumns,
    setPersistedVisibleColumns,
    persistedColumnOrder,
    setPersistedColumnOrder,
    persistedColumnSizing,
    setPersistedColumnSizing,
  } = useSettingsStore();
  const { selectedClaim, setSelectedClaim, setShowPreview, showClaimTotals } =
    useSelectedClaimStore();
  const { tariffRowFilterMode } = useClaimTabFilterStore();

  const {
    manualLoading,
    setManualLoading,
    expanded,
    setExpanded,
    tableInstance,
    setTableInstance,
    createModalType,
    setCreateModalType,
    quickCreatePrefill,
    setQuickCreatePrefill,
    useSelectedClaimForDuplicate,
    setUseSelectedClaimForDuplicate,
    rowSelection,
    setRowSelection,
    contextMenuPosition,
    setContextMenuPosition,
  } = useClaimTableState();

  const {
    bulkCancelClaimIDs,
    setBulkCancelClaimIDs,
    bulkCancelOpen,
    setBulkCancelOpen,
    bulkEditClaimIDs,
    setBulkEditClaimIDs,
    bulkEditOpen,
    setBulkEditOpen,
    bulkReconcileOpen,
    setBulkReconcileOpen,
    bulkReconcileRows,
    setBulkReconcileRows,
    bulkResubmitOpen,
    setBulkResubmitOpen,
    bulkResubmitRows,
    setBulkResubmitRows,
    bulkReadyAction,
    setBulkReadyAction,
  } = useClaimBulkState();

  const serverClaims: Claim[] = claims.data ?? [];

  // Mark this tab/window as the reusable claims target so task links using
  // target="claims-window" can route here in split-view setups.
  useEffect(() => {
    window.name = CLAIMS_WINDOW_TARGET;
  }, []);

  useEffect(() => {
    // Fallback for browsers/environments that do not support cross-tab messaging.
    if (typeof BroadcastChannel === 'undefined') return;

    // Listen for quick-create navigation requests sent from the Task window.
    const channel = new BroadcastChannel(CLAIMS_WINDOW_CHANNEL);
    const onMessage = (event: MessageEvent) => {
      const message = event.data as
        | { type?: string; requestID?: string; search?: string }
        | undefined;
      if (message?.type !== 'claims:quick-create-request') return;

      // Acknowledge receipt so the sender knows an existing Claims window handled it.
      channel.postMessage({
        type: 'claims:quick-create-ack',
        requestID: message.requestID,
      });

      // Normalize incoming query text to a valid `?key=value` search string.
      const rawSearch = message.search ?? '';
      const search = rawSearch.startsWith('?')
        ? rawSearch
        : rawSearch
        ? `?${rawSearch}`
        : '';

      // Navigate this Claims window to the requested quick-create URL.
      navigate(
        {
          pathname: '/claims',
          search,
        },
        { replace: false }
      );
    };

    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [navigate]);

  const isClaimsModifyPermissionResolved =
    isAdmin || !currentUserID || currentUserRole !== null;
  const setStepperCurrent = useClaimEditStepperStore(
    (state) => state.setCurrentById
  );
  const clearStepperQueue = useClaimEditStepperStore((state) => state.clear);

  const appliedQuickCreateKeyRef = useRef<string | null>(null);
  const handleCloseContextMenu = () => setContextMenuPosition(null);
  const handleClearTariffSelection = () => setRowSelection({});
  const closeBulkCancelModal = () => {
    setBulkCancelOpen(false);
    setBulkCancelClaimIDs([]);
  };
  const closeBulkEditModal = () => {
    setBulkEditOpen(false);
    setBulkEditClaimIDs([]);
  };
  const closeBulkEditTableModal = () => {
    tableInstance?.setEditingRow(null);
    closeBulkEditModal();
  };
  const closeBulkResubmitModal = () => {
    setBulkResubmitOpen(false);
    setBulkResubmitRows([]);
    setBulkReadyAction('resubmit');
  };
  const openBulkResubmitModalForRows = (
    rows: ClaimTableRow[],
    action: 'ready' | 'resubmit' = 'resubmit'
  ) => {
    if (!rows.length) return;
    setBulkResubmitRows(rows);
    setBulkReadyAction(action);
    setBulkResubmitOpen(true);
  };

  const dirtyFields = useRef<{
    patient: boolean;
    provider: boolean;
    tariffs: boolean;
    status: boolean;
    site: boolean;
    healthcard: boolean;
    __stagedTariffActions?: boolean;
  }>({
    patient: false,
    provider: false,
    tariffs: false,
    status: false,
    site: false,
    healthcard: false,
  });
  const stagedTariffActionsRef = useRef<{
    claimID: string | null;
    hasStagedActions: boolean;
  }>({
    claimID: null,
    hasStagedActions: false,
  });
  const resetDirtyFields = () => {
    dirtyFields.current = {
      patient: false,
      provider: false,
      tariffs: false,
      status: false,
      site: false,
      healthcard: false,
    };
  };
  const clearStagedTariffActions = () => {
    stagedTariffActionsRef.current = {
      claimID: null,
      hasStagedActions: false,
    };
  };
  const confirmDiscardStagedTariffActions = (claimID?: string | null) =>
    !(
      stagedTariffActionsRef.current.hasStagedActions &&
      stagedTariffActionsRef.current.claimID === claimID
    ) ||
    window.confirm(
      'You have staged tariff actions that have not been saved. Close without saving?'
    );
  const closeEditModalFromShell = () => {
    if (bulkEditOpen) {
      closeBulkEditTableModal();
      return;
    }

    const currentEditing = tableInstance?.getState()?.editingRow as
      | MRT_Row<ClaimTableRow>
      | undefined;
    const currentClaimID =
      getParentClaim(currentEditing?.original)?.id || editingId;

    if (!confirmDiscardStagedTariffActions(currentClaimID)) return;

    tableInstance?.setEditingRow(null);
    clearStagedTariffActions();
    resetDirtyFields();
    navigate('/claims');
  };

  const {
    handleCreateClaim,
    handleBulkClaimCreation,
    handleUpdateClaim,
    handleCreateSubmission,
    queryPending,
  } = useClaimHandlers({
    dirtyFields,
    resetDirtyFields,
  });

  const {
    actionRequestOpen,
    setActionRequestOpen,
    historyOpen,
    setHistoryOpen,
    debitOpen,
    setDebitOpen,
    submissionOpen,
    setSubmissionOpen,
    viewBatchOpen,
    setViewBatchOpen,
    cancelClaimOpen,
    setCancelClaimOpen,
    invoiceOpen,
    setInvoiceOpen,
    queryModalOpen,
    setQueryModalOpen,
    invoiceModalOpen,
    setInvoiceModalOpen,
    resubmitOpen,
    setResubmitOpen,
    reconcileOpen,
    setReconcileOpen,
    matchOpen,
    setMatchOpen,
    writeOffOpen,
    setWriteOffOpen,
    markReadyOpen,
    setMarkReadyOpen,
    markPaidOpen,
    setMarkPaidOpen,
    markIncompleteOpen,
    setMarkIncompleteOpen,
    markHistoricalOpen,
    setMarkHistoricalOpen,
    markUninsuredOpen,
    setMarkUninsuredOpen,
    markPendingWriteOffOpen,
    setMarkPendingWriteOffOpen,
    internalCommentOpen,
    setInternalCommentOpen,
    receiptOpen,
    setReceiptOpen,
    viewActionsOpen,
    setViewActionsOpen,
    sourceDataOpen,
    setSourceDataOpen,
    attachmentsOpen,
    setAttachmentsOpen,
    supportingDocumentOpen,
    setSupportingDocumentOpen,
  } = useClaimRowModalState();

  const {
    tableData,
    tableRows: allTableRows,
    confidenceByClaimID,
    confidenceMetadataByClaimID,
    getClaimByID,
  } = useClaimTableData({
    claims: serverClaims,
    editingID: editingId,
  });

  // On mount, scrub invalid filters (e.g. incomplete date range)
  useEffect(() => {
    const scrubbedFilters = pagination?.columnFilters?.filter((data) => {
      if (['serviceStart', 'createdAt'].includes(data.id)) {
        return Array.isArray(data.value) && data.value[0] && data.value[1];
      }
      // keep filters that have a value or are not empty
      return data.value !== undefined && data.value !== '';
    });
    if (scrubbedFilters?.length > 0) {
      setPagination({
        ...pagination,
        columnFilters: scrubbedFilters,
      });
    }
  }, []);

  const appliedQueryFilterRef = useRef<string | null>(null);

  // Allow opening /claims with URL-defined column filters.
  useEffect(() => {
    const queryKey = searchParams.toString();
    const queryFilters = getClaimColumnFiltersFromSearchParams(searchParams);
    if (queryFilters === null) {
      appliedQueryFilterRef.current = null;
      return;
    }

    const hasQueryFilters = hasAllClaimColumnFilters(
      pagination.columnFilters,
      queryFilters
    );

    // If query filters are already present, mark this query as applied so
    // users can manually adjust filters afterward without being reset.
    if (hasQueryFilters) {
      appliedQueryFilterRef.current = queryKey;
      return;
    }

    // If we previously applied this query and query filters are now absent,
    // assume the user intentionally changed filters.
    if (appliedQueryFilterRef.current === queryKey) return;

    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
      globalFilter: '',
      columnFilters: mergeClaimColumnFilters(prev.columnFilters, queryFilters),
    }));
  }, [searchParams, pagination.columnFilters, setPagination]);

  useEffect(() => {
    if (!tableInstance) return;

    const createType = getQuickCreateTypeFromSearchParams(searchParams);
    if (!createType) {
      appliedQuickCreateKeyRef.current = null;
      return;
    }

    const queryKey = searchParams.toString();
    if (!isClaimsModifyPermissionResolved) return;

    if (!canModifyClaims) {
      if (appliedQuickCreateKeyRef.current === queryKey) return;
      appliedQuickCreateKeyRef.current = queryKey;
      const cleanedParams = clearQuickCreateSearchParams(searchParams);
      navigate(
        {
          pathname: '/claims',
          search: cleanedParams.toString()
            ? `?${cleanedParams.toString()}`
            : '',
        },
        { replace: true }
      );
      return;
    }

    if (payors.length === 0 || !site.id) return;

    if (appliedQuickCreateKeyRef.current === queryKey) return;
    const providerID = searchParams.get(QUICK_CREATE_PARAM_KEYS.providerID);
    const intakeItemID = searchParams.get(QUICK_CREATE_PARAM_KEYS.intakeItemID);
    const requestedSiteID = searchParams.get(QUICK_CREATE_PARAM_KEYS.siteID);
    const serviceDate = searchParams.get(QUICK_CREATE_PARAM_KEYS.serviceDate);
    const matchingProvider = providerID
      ? providers.find((provider) => provider.id === providerID) ?? null
      : null;

    // Wait for provider lookup before opening quick-create so provider fields
    // are pre-populated reliably.
    if (providerID && !matchingProvider) return;

    const parsedServiceDate = serviceDate
      ? parseDateTime(serviceDate, { zone: site.timezone })
      : null;

    const hasServiceDate = parsedServiceDate?.isValid ?? false;
    const startDateTime = hasServiceDate
      ? serviceDate?.includes('T')
        ? parsedServiceDate!
        : parsedServiceDate!.startOf('day')
      : null;
    const serviceStart = startDateTime?.toISO() ?? undefined;
    const serviceEnd =
      startDateTime?.plus({ minutes: 15 }).toISO() ?? undefined;
    const createdAt = serviceStart;
    const quickSiteID =
      requestedSiteID && requestedSiteID === site.id ? site.id : undefined;

    const createInitialValues: Partial<CreateClaimForm> = {};
    const bulkSharedInitialValues: Partial<SharedClaimForm> = {};

    if (providerID) {
      createInitialValues.providerID = providerID;
      bulkSharedInitialValues.providerID = providerID;
    }
    if (matchingProvider) {
      createInitialValues.provider = matchingProvider as any;
      bulkSharedInitialValues.provider = matchingProvider as any;
    }
    if (quickSiteID) {
      createInitialValues.siteID = quickSiteID;
      bulkSharedInitialValues.siteID = quickSiteID;
    }
    if (intakeItemID) {
      createInitialValues.intakeItemID = intakeItemID;
      bulkSharedInitialValues.intakeItemID = intakeItemID;
    }
    if (serviceStart) {
      createInitialValues.serviceStart = serviceStart;
      createInitialValues.createdAt = createdAt;
      createInitialValues.serviceEnd = serviceEnd;
      bulkSharedInitialValues.serviceStart = serviceStart;
      bulkSharedInitialValues.createdAt = createdAt;
      bulkSharedInitialValues.serviceEnd = serviceEnd;
    }

    setQuickCreatePrefill({
      createInitialValues,
      bulkSharedInitialValues,
    });
    setUseSelectedClaimForDuplicate(false);
    setCreateModalType(createType);
    tableInstance.setCreatingRow(true);
    appliedQuickCreateKeyRef.current = queryKey;

    const cleanedParams = clearQuickCreateSearchParams(searchParams);

    navigate(
      {
        pathname: '/claims',
        search: cleanedParams.toString() ? `?${cleanedParams.toString()}` : '',
      },
      { replace: true }
    );
  }, [
    navigate,
    payors.length,
    providers,
    searchParams,
    canModifyClaims,
    isClaimsModifyPermissionResolved,
    site.id,
    tableInstance,
  ]);

  useEffect(() => {
    if (editingId) {
      setStepperCurrent(editingId);
    } else {
      clearStepperQueue();
    }
  }, [editingId, setStepperCurrent, clearStepperQueue]);

  const { markTariffsAs, resubmitTariffs, writeOffTariffs } =
    useClaimTariffActions({
      getClaimById: getClaimByID,
      onActionSuccess: () => {
        refetchClaims();
        refetchStatusesCount();
      },
    });

  const openReconcileModalForClaim = (claim: Claim) => {
    const claimRow = isClaimParentRow(claim)
      ? claim
      : allTableRows.find((row) => row.id === claim.id);
    const reconcilableTariffRows = (claimRow?.subRows ?? []).filter(
      (tariffRow) =>
        isReconcileEligibleTariffStatus(tariffRow.tariffs?.[0]?.status)
    );

    if (!reconcilableTariffRows.length) return;

    setBulkReconcileRows(reconcilableTariffRows);
    setBulkReconcileOpen(true);
  };

  const columns = ClaimColumns<Claim>({
    providerProp: providers || [],
    siteProp: sites || [],
    payorsProp: payors || [],
    remittancesProp: remittances || [],
    eobsProp: eobs || [],
    eobCountsProp: eobCounts || {},
    confidenceByClaimID,
    onEobFilterOpen: loadEobLookups,
    claimStatuses,
    actionColumnRows: tableData,
    setSelectedClaim,
    onOpenClaimEdit: (claim) => navigate(`/claims/edit/${claim.id}`),
    setHistoryOpen,
    setDebitOpen,
    setSubmissionOpen,
    setViewBatchOpen,
    setCancelClaimOpen,
    invalidData,
    setInvoiceOpen,
    setQueryModalOpen,
    setInvoiceModalOpen,
    setResubmitOpen,
    setReconcileOpen,
    setMatchOpen,
    setWriteOffOpen,
    setMarkReadyOpen,
    setMarkPaidOpen,
    setMarkIncompleteOpen,
    setMarkHistoricalOpen,
    setMarkUninsuredOpen,
    setMarkPendingWriteOffOpen,
    setInternalCommentOpen,
    setReceiptOpen,
    setActionRequestOpen,
    setViewActionsOpen,
    setSourceDataOpen,
    setAttachmentsOpen,
    setSupportingDocumentOpen,
    onClaimReconcile: openReconcileModalForClaim,
    onDuplicateClaim: () => handleDuplicateClaim(),
    dirtyFields,
    stagedTariffActionsRef,
    isAdmin,
    isProvider,
    isAllSites,
    onTariffResubmit: ({ claimID, tariffID }) => {
      const row = getTariffRowByIDs(claimID, tariffID);
      if (!row) return;
      openBulkResubmitModalForRows([row]);
    },
    onTariffReconcile: ({ claimID, tariffID }) => {
      const row = getTariffRowByIDs(claimID, tariffID);
      if (!row) return;
      openReconcileModalForRows([row]);
    },
    onTariffWriteOff: async ({ claimID, tariffID }) => {
      await writeOffTariffs({
        claimID,
        tariffIDs: [tariffID],
      });
    },
    onTariffMarkAs: async ({ claimID, tariffID, status: tariffStatus }) => {
      const row = getTariffRowByIDs(claimID, tariffID);
      if (
        tariffStatus === CLAIM_TARIFF_STATUS.READY &&
        row &&
        getClaimTariffRowDebits(row).some(
          (debit) => Number(debit.paidAmount ?? 0) > 0
        )
      ) {
        openBulkResubmitModalForRows([row], 'ready');
        return;
      }
      await markTariffsAs({
        claimID,
        tariffIDs: [tariffID],
        status: tariffStatus,
      });
    },
  }) as unknown as MRT_ColumnDef<ClaimTableRow>[];

  const tableRows = useMemo(
    () =>
      applyClaimTariffRowFilters(
        allTableRows,
        columns,
        pagination.columnFilters ?? [],
        tariffRowFilterMode === 'showMatchingTariffsOnly',
        editingId
      ),
    [
      allTableRows,
      columns,
      pagination.columnFilters,
      tariffRowFilterMode,
      editingId,
    ]
  );

  useEffect(() => {
    const validRowIds = new Set<string>();
    tableRows.forEach((row) => {
      validRowIds.add(row.rowId);
      validRowIds.add(row.id);
      (row.subRows ?? []).forEach((subRow) => {
        validRowIds.add(subRow.rowId);
        validRowIds.add(subRow.id);
      });
    });

    setRowSelection((prev) => {
      const nextEntries = Object.entries(prev).filter(
        ([rowId, selected]) => Boolean(selected) && validRowIds.has(rowId)
      );
      if (nextEntries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(nextEntries);
    });
    if (!tableRows.length) setContextMenuPosition(null);
  }, [setContextMenuPosition, setRowSelection, tableRows]);

  const needsProviderColumnDefaults =
    isProvider && providerClaimColumnLayoutVersion < 1;

  const initialColumns = useMemo(
    () =>
      getDefaultColumnVisibility(
        columns as unknown as MRT_ColumnDef<Claim>[],
        getDefaultVisibleColumns(isProvider),
        needsProviderColumnDefaults ? null : persistedVisibleColumns,
        isProvider,
        isAllSites
      ),
    [
      columns,
      persistedVisibleColumns,
      isProvider,
      needsProviderColumnDefaults,
      isAllSites,
    ]
  );

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    () => initialColumns
  );

  useEffect(() => {
    if (!isProvider) return;

    setVisibleColumns((currentVisibility) => {
      if (currentVisibility.siteID === isAllSites) {
        return currentVisibility;
      }

      return { ...currentVisibility, siteID: isAllSites };
    });
  }, [isAllSites, isProvider]);

  useEffect(() => {
    setVisibleColumns((previous) => {
      const keys = Object.keys(initialColumns);
      if (
        keys.length === Object.keys(previous).length &&
        keys.every((key) => previous[key] === initialColumns[key])
      )
        return previous;
      return initialColumns;
    });
  }, [initialColumns]);

  const baseColumnOrder: string[] = useMemo(() => {
    const columnKeys = columns
      .map((c) => c.accessorKey as string)
      .filter(Boolean);
    if (isProvider) return getProviderColumnOrder(columnKeys);
    if (isAdmin) return columnKeys;
    return [
      ...BILLER_CLAIM_COLUMN_ORDER.filter((key) => columnKeys.includes(key)),
      ...columnKeys.filter((key) => !BILLER_CLAIM_COLUMN_ORDER.includes(key)),
    ];
  }, [columns, isProvider, isAdmin]);

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (
      needsProviderColumnDefaults ||
      !persistedColumnOrder ||
      persistedColumnOrder.length === 0
    ) {
      return baseColumnOrder;
    }

    // Ensure persisted column order keys exist in the base column order
    const existing = persistedColumnOrder.filter((key) =>
      baseColumnOrder.includes(key)
    );
    // Add any new columns that are not in the persisted order
    const added = baseColumnOrder.filter((key) => !existing.includes(key));
    return [...existing, ...added];
  });

  // Sync column order when base columns or persisted order changes
  useEffect(() => {
    setColumnOrder((prev) => {
      const defaultOrder = isProvider ? baseColumnOrder : prev;
      const source =
        !needsProviderColumnDefaults &&
        persistedColumnOrder &&
        persistedColumnOrder.length > 0
          ? persistedColumnOrder
          : defaultOrder;
      const existing = source.filter((key) => baseColumnOrder.includes(key));
      const added = baseColumnOrder.filter((key) => !existing.includes(key));
      const next = [...existing, ...added];
      const isEqual =
        next.length === prev.length && next.every((k, idx) => k === prev[idx]);
      return isEqual ? prev : next;
    });
  }, [
    baseColumnOrder,
    persistedColumnOrder,
    isProvider,
    needsProviderColumnDefaults,
  ]);

  useEffect(() => {
    if (!needsProviderColumnDefaults) return;
    applyProviderClaimColumnDefaults(initialColumns, baseColumnOrder);
  }, [
    needsProviderColumnDefaults,
    applyProviderClaimColumnDefaults,
    initialColumns,
    baseColumnOrder,
  ]);

  // Enhance columns with any dynamic properties ie filtering, sorting, etc
  enhanceColumns(
    columns as unknown as MRT_ColumnDef<Claim>[],
    isProvider,
    isAllSites
  );

  const handleCreatingRowSave = async (props: {
    exitCreatingMode: () => void;
    row: MRT_Row<ClaimTableRow>;
    table: MRT_TableInstance<ClaimTableRow>;
    values: Record<string, any>;
  }) => {
    if (createModalType === 'claim') {
      props.values.createdBy = currentUserID;
      props.row._valuesCache = props.values;
      await handleCreateClaim({
        values: props.values as Claim,
        table: props.table as unknown as MRT_TableInstance<Claim>,
      });
    } else if (createModalType === 'repeat') {
      await handleBulkClaimCreation({
        siteID: site.id,
        createdBy: currentUserID!,
        values: props.values as Claim[],
        table: props.table as unknown as MRT_TableInstance<Claim>,
      });
    } else if (createModalType === 'bulk') {
      await handleBulkClaimCreation({
        siteID: site.id,
        createdBy: currentUserID!,
        values: props.values as Claim[],
        table: props.table as unknown as MRT_TableInstance<Claim>,
      });
    }
    setQuickCreatePrefill(null);
    setUseSelectedClaimForDuplicate(false);
    props.exitCreatingMode();
  };

  const handleTableContextMenu: React.MouseEventHandler<HTMLTableElement> = (
    event
  ) => {
    const hasSelectedRows = hasTariffSelection || selectedClaimIDs.length > 0;
    if (isProvider || isAdmin || !tableRows.length || !hasSelectedRows) return;
    event.preventDefault();
    event.stopPropagation();
    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const selectClaimRows = (claimId: string) => {
    const claimRow = tableRows.find((row) => row.id === claimId);
    if (!claimRow) return;
    const claimRowId = claimRow.rowId || buildClaimRowId(claimId);
    const childRowIds = (claimRow.subRows ?? []).map((subRow) => subRow.rowId);

    setRowSelection((prev) => {
      const next = { ...prev };
      delete next[claimRowId];
      return {
        ...next,
        ...buildClaimRowSelection(claimRowId, true, childRowIds),
      };
    });
  };

  const handleReconcileSelected = async () => {
    if (!tableInstance) {
      handleCloseContextMenu();
      return;
    }
    const selectedRows = tableInstance.getSelectedRowModel().rows ?? [];
    if (!selectedRows.length) {
      handleCloseContextMenu();
      return;
    }
    setManualLoading(true);
    try {
      const { reconciledClaims } = await bulkReconcileTariffs({
        rows: selectedRows as unknown as MRT_Row<ClaimTableRow>[],
        bulkReconcileTariffsMutation,
      });
      if (reconciledClaims > 0) {
        refetchClaims();
        refetchStatusesCount();
      }
    } catch (e) {
      void e;
    } finally {
      setManualLoading(false);
      handleCloseContextMenu();
    }
  };

  const getUnlockedClaimIDs = (claimIDs: string[]): string[] =>
    claimIDs.filter((claimID) => {
      const claim = getClaimByID(claimID);
      return claim && !claimHasTariffStatus(claim, CLAIM_TARIFF_STATUS.PENDING);
    });

  const getSelectedTariffRows = () => {
    const selectedTariffRowIds = new Set(
      getTariffSelectionFromRowSelection(rowSelection).map((selection) =>
        buildTariffRowId(selection.claimID, selection.tariffID)
      )
    );
    return tableRows
      .flatMap((claimRow) => claimRow.subRows ?? [])
      .filter(
        (tariffRow) =>
          selectedTariffRowIds.has(tariffRow.rowId) &&
          tariffRow.tariffs[0]?.status !== CLAIM_TARIFF_STATUS.PENDING
      );
  };

  const getSelectedReconcileEligibleTariffRows = () => {
    const selectedClaimIDSet = new Set(
      getClaimIdsFromRowSelection(rowSelection)
    );
    const selectedTariffRowIDSet = new Set(
      getTariffSelectionFromRowSelection(rowSelection).map((selection) =>
        buildTariffRowId(selection.claimID, selection.tariffID)
      )
    );

    return buildClaimTableRows(tableData)
      .flatMap((claimRow) => claimRow.subRows ?? [])
      .filter((tariffRow) => {
        const isSelected =
          selectedClaimIDSet.has(tariffRow.parentClaimId) ||
          selectedTariffRowIDSet.has(tariffRow.rowId);
        const tariffStatus = tariffRow.tariffs[0]?.status;

        return (
          isSelected && isReconcileEligibleTariffStatus(tariffStatus?.trim())
        );
      });
  };

  const openReconcileModalForRows = (rows: ClaimTableRow[]) => {
    if (!rows.length) return;
    setBulkReconcileRows(rows);
    setBulkReconcileOpen(true);
  };

  const handleOpenReconcileSelectedTariffs = () => {
    const reconcileEligibleTariffRows =
      getSelectedReconcileEligibleTariffRows();
    if (!reconcileEligibleTariffRows.length) {
      handleCloseContextMenu();
      return;
    }
    openReconcileModalForRows(reconcileEligibleTariffRows);
    handleCloseContextMenu();
  };

  const handleOpenResubmitSelectedTariffs = () => {
    const selectedTariffRows = getSelectedTariffRows();
    if (!selectedTariffRows.length) {
      handleCloseContextMenu();
      return;
    }
    openBulkResubmitModalForRows(selectedTariffRows);
    handleCloseContextMenu();
  };

  const getTariffRowByIDs = (claimID: string, tariffID: string) =>
    tableRows
      .flatMap((claimRow) => claimRow.subRows ?? [])
      .find(
        (tariffRow) =>
          tariffRow.parentClaimId === claimID && tariffRow.tariffId === tariffID
      );

  const handleConfirmBulkReconcile = async (
    recordAdjustment: boolean,
    comment?: string,
    internalComment?: string
  ) => {
    if (!bulkReconcileRows.length) {
      setBulkReconcileOpen(false);
      return;
    }

    setManualLoading(true);
    try {
      const { reconciledClaims } = await bulkReconcileTariffs({
        rows: bulkReconcileRows.map((row) => ({
          original: row,
        })) as unknown as MRT_Row<ClaimTableRow>[],
        bulkReconcileTariffsMutation,
        recordAdjustment,
        comment,
        internalComment,
      });
      if (reconciledClaims > 0) {
        refetchClaims();
        refetchStatusesCount();
      }
      setBulkReconcileOpen(false);
      setBulkReconcileRows([]);
    } finally {
      setManualLoading(false);
    }
  };

  const handleConfirmBulkResubmit = async () => {
    if (!bulkResubmitRows.length) {
      closeBulkResubmitModal();
      return;
    }

    setManualLoading(true);
    try {
      const byClaim = bulkResubmitRows.reduce<Record<string, string[]>>(
        (acc, row) => {
          const claimID = row.parentClaimId || row.parentClaim?.id;
          const tariffID = row.tariffId || row.tariffs?.[0]?.id;
          if (!claimID || !tariffID) return acc;
          acc[claimID] = [...(acc[claimID] ?? []), tariffID];
          return acc;
        },
        {}
      );

      await Promise.all(
        Object.entries(byClaim).map(([claimID, tariffIDs]) =>
          bulkReadyAction === 'ready'
            ? markTariffsAs({
                claimID,
                tariffIDs: [...new Set(tariffIDs)],
                status: CLAIM_TARIFF_STATUS.READY,
              })
            : resubmitTariffs({
                claimID,
                tariffIDs: [...new Set(tariffIDs)],
              })
        )
      );
    } catch (e) {
      void e;
    } finally {
      setManualLoading(false);
      closeBulkResubmitModal();
    }
  };

  const handleMarkSelectedTariffs = async (tariffStatus: string) => {
    if (hasPendingTariffSelection) return;
    if (tariffStatus === CLAIM_TARIFF_STATUS.READY) {
      const selectedTariffRows = getSelectedTariffRows();
      const rowsWithPayments = selectedTariffRows.filter((row) =>
        getClaimTariffRowDebits(row).some(
          (debit) => Number(debit.paidAmount ?? 0) > 0
        )
      );
      if (rowsWithPayments.length > 0) {
        openBulkResubmitModalForRows(selectedTariffRows, 'ready');
        handleCloseContextMenu();
        return;
      }
    }
    const items = getTariffSelectionFromRowSelection(rowSelection);
    const byClaim = items.reduce<Record<string, string[]>>((acc, item) => {
      acc[item.claimID] = [...(acc[item.claimID] ?? []), item.tariffID];
      return acc;
    }, {});
    await Promise.all(
      Object.entries(byClaim).map(([claimID, tariffIDs]) =>
        markTariffsAs({
          claimID,
          tariffIDs,
          status: tariffStatus,
        })
      )
    );
    handleCloseContextMenu();
  };

  const handleOpenBulkCancel = () => {
    const claimIDs = getUnlockedClaimIDs(
      getClaimIdsFromRowSelection(rowSelection)
    );
    if (!claimIDs.length) {
      handleCloseContextMenu();
      return;
    }

    setBulkCancelClaimIDs(claimIDs);
    setBulkCancelOpen(true);
    handleCloseContextMenu();
  };

  const handleConfirmBulkCancel = async () => {
    const claimIDs = getUnlockedClaimIDs(bulkCancelClaimIDs);
    if (!claimIDs.length) {
      closeBulkCancelModal();
      return;
    }

    setManualLoading(true);
    try {
      const cancelledClaimIDs = await bulkCancelClaimsMutation.mutateAsync({
        claimIDs,
      });
      if (cancelledClaimIDs.length > 0) {
        refetchClaims();
        refetchStatusesCount();
      }
    } finally {
      setManualLoading(false);
    }
  };

  const handleOpenBulkEdit = () => {
    const claimIDs = getUnlockedClaimIDs(
      getClaimIdsFromRowSelection(rowSelection)
    );
    const selectedParentRow = tableInstance
      ?.getSelectedRowModel()
      .rows.find(
        (selectedRow) =>
          isClaimParentRow(selectedRow.original) &&
          claimIDs.includes(selectedRow.original.id)
      );

    if (!claimIDs.length || !canModifyClaims || !selectedParentRow) {
      handleCloseContextMenu();
      return;
    }

    setBulkEditClaimIDs(claimIDs);
    setBulkEditOpen(true);
    tableInstance?.setEditingRow(selectedParentRow);
    handleCloseContextMenu();
  };

  const handleConfirmBulkEdit = async ({
    claimIDs,
    values,
  }: BulkClaimEditPayload) => {
    const unlockedClaimIDs = getUnlockedClaimIDs(claimIDs);
    if (!unlockedClaimIDs.length || !Object.keys(values).length) return;

    setManualLoading(true);
    try {
      await bulkEditClaimsMutation.mutateAsync({
        claimIDs: unlockedClaimIDs,
        values,
      });
      closeBulkEditTableModal();
      setRowSelection({});
      refetchClaims();
      refetchStatusesCount();
    } finally {
      setManualLoading(false);
    }
  };

  // TODO: I think this is wrong and will need to figure it out later
  const handleDuplicateClaim = () => {
    if (payors.length === 0 || !site.id) return;
    setQuickCreatePrefill(null);
    setCreateModalType('claim');
    setUseSelectedClaimForDuplicate(true);
    tableInstance?.setCreatingRow(true);
  };

  const tableOptions = getClaimTableOptions({
    tableRows,
    columns,
    pagination,
    setPagination,
    visibleColumns,
    setVisibleColumns,
    initialColumns,
    expanded,
    setExpanded,
    rowSelection,
    setRowSelection,
    columnOrder,
    setColumnOrder,
    persistedColumnSizing,
    isFetching,
    isLoading,
    queryPending,
    manualLoading,
    isError,
    errorMessage,
    isProvider,
    isAdmin,
    mode,
    theme,
    providers,
    payors,
    site,
    status,
    claimStatuses,
    providerActionRequestCount,
    actionNeededCount,
    rowCount: claims.total,
    payorTariffCounts: claims.payorTariffCounts ?? {},
    queryClient,
    navigate,
    handleUpdateClaim,
    handleCreatingRowSave,
    resetDirtyFields,
    dirtyFields,
    confidenceMetadataByClaimID,
    stagedTariffActionsRef,
    clearStagedTariffActions,
    selectClaimRows,
    setSelectedClaim,
    setShowPreview,
    setViewActionsOpen: setViewActionsOpen,
    useSelectedClaimForDuplicate,
    setUseSelectedClaimForDuplicate,
    quickCreatePrefill,
    setQuickCreatePrefill,
    createModalType,
    closeEditModalFromShell,
    bulkEditOpen,
    bulkEditClaimIDs,
    closeBulkEditModal: closeBulkEditTableModal,
    handleConfirmBulkEdit,
    handleTableContextMenu,
    setPersistedVisibleColumns,
    setPersistedColumnOrder,
    setPersistedColumnSizing,
    mantineTableBodyRowProps,
  });

  // Sync the route param to edit modal state.
  useEffect(() => {
    if (!tableInstance) return;
    const currentEditing = tableInstance.getState()?.editingRow;
    if (editingId) {
      const row = tableInstance
        .getRowModel()
        .rows.find(
          (r: any) =>
            r.original?.id === editingId && isClaimParentRow(r.original)
        );
      if (row && currentEditing?.id !== row.id) {
        // ensure selection & preview state mirror manual double-click behavior
        const claimRowId = row.original.rowId || buildClaimRowId(editingId);
        const subRowIds = (row.original.subRows ?? []).map(
          (subRow: ClaimTableRow) => subRow.rowId
        );
        tableInstance.setRowSelection?.(
          buildClaimRowSelection(claimRowId, true, subRowIds)
        );
        setSelectedClaim(getParentClaim(row.original));
        setShowPreview(true);
        tableInstance.setEditingRow(row);
      }
      // When the row is missing, we rely on fetchedClaim + tableData to inject it.
    } else if (currentEditing && !bulkEditOpen) {
      tableInstance.setEditingRow(null);
    }
  }, [bulkEditOpen, editingId, tableInstance, tableData]);

  // Derived UI state for context menu actions
  const selectedTariffSelections =
    getTariffSelectionFromRowSelection(rowSelection);
  const selectedClaimIDs = getClaimIdsFromRowSelection(rowSelection);
  const selectedClaims = selectedClaimIDs.map((claimID) =>
    tableData.find((claim) => claim.id === claimID)
  );
  const selectedTariffStatuses = !isProvider
    ? selectedTariffSelections.map((selection) => {
        const claim = (tableData || []).find(
          (row) => row.id === selection.claimID
        );
        const tariff = claim?.tariffs?.find((t) => t.id === selection.tariffID);
        return isClaimTariffStatusValue(tariff?.status)
          ? tariff.status
          : undefined;
      })
    : [];
  const hasTariffSelection = !isProvider && getSelectedTariffRows().length > 0;
  const hasPendingTariffSelection = selectedTariffStatuses.includes(
    CLAIM_TARIFF_STATUS.PENDING
  );
  const hasCancellableClaimSelection =
    !isProvider &&
    selectedClaims.some(
      (claim) =>
        claim &&
        !claimHasTariffStatus(claim, CLAIM_TARIFF_STATUS.PENDING) &&
        claim.tariffs?.some(
          (tariff) => tariff.status?.trim() !== CLAIM_TARIFF_STATUS.CANCELLED
        )
    );
  const hasBulkEditableClaimSelection =
    !isProvider &&
    canModifyClaims &&
    isClaimsModifyPermissionResolved &&
    getUnlockedClaimIDs(selectedClaimIDs).length > 0;
  const hasReconcilableTariffSelection =
    !isProvider && getSelectedReconcileEligibleTariffRows().length > 0;
  const hasResubmittableTariffSelection =
    !isProvider &&
    hasTariffSelection &&
    areTariffStatusesEligible(
      selectedTariffStatuses.filter(
        (status) => status !== CLAIM_TARIFF_STATUS.PENDING
      ),
      isResubmittableTariffStatus
    );

  const formatCurrency = (value: number) =>
    Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(Number(value) || 0);

  const currentProvider = providers.find(
    (provider) => provider.id === currentProviderID
  );
  const providerName = currentProvider
    ? [
        currentProvider.title,
        currentProvider.firstName,
        currentProvider.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    : [currentUserFirstName, currentUserLastName].filter(Boolean).join(' ');
  const title = isProvider
    ? translate('pages:claims.providerTitle', 'Provider claims')
    : translate('pages:claims.label', 'Claims');

  return (
    <>
      {!isProvider && !isAdmin && (
        <Global styles={getBillerClaimsStyles(theme, mode)} />
      )}
      <ClaimBulkResubmitModal
        open={bulkResubmitOpen}
        rows={bulkResubmitRows}
        onClose={closeBulkResubmitModal}
        onConfirm={handleConfirmBulkResubmit}
        action={bulkReadyAction}
      />
      <ClaimBulkReconcileModal
        open={bulkReconcileOpen}
        rows={bulkReconcileRows}
        onClose={() => {
          setBulkReconcileOpen(false);
          setBulkReconcileRows([]);
        }}
        onConfirm={handleConfirmBulkReconcile}
      />
      <ClaimBulkCancelModal
        open={bulkCancelOpen}
        claimCount={bulkCancelClaimIDs.length}
        onClose={closeBulkCancelModal}
        onConfirm={handleConfirmBulkCancel}
      />
      <ClaimsContextMenu
        contextMenuPosition={contextMenuPosition}
        onClose={handleCloseContextMenu}
        selectedTariffCount={selectedTariffSelections.length}
        selectedClaimCount={selectedClaimIDs.length}
        hasTariffSelection={hasTariffSelection}
        hasPendingTariffSelection={hasPendingTariffSelection}
        hasCancellableClaimSelection={hasCancellableClaimSelection}
        hasBulkEditableClaimSelection={hasBulkEditableClaimSelection}
        hasReconcilableTariffSelection={hasReconcilableTariffSelection}
        hasResubmittableTariffSelection={hasResubmittableTariffSelection}
        onResubmitSelectedTariffs={handleOpenResubmitSelectedTariffs}
        onReconcileSelectedTariffs={handleOpenReconcileSelectedTariffs}
        onMarkSelectedTariffs={handleMarkSelectedTariffs}
        onCancelSelectedClaims={handleOpenBulkCancel}
        onEditSelectedClaims={handleOpenBulkEdit}
        onClearSelection={handleClearTariffSelection}
        mode={mode}
        theme={theme}
      />
      <ClaimRowModals
        selectedClaim={selectedClaim}
        setSelectedClaim={setSelectedClaim}
        status={status}
        providers={providers}
        payors={payors}
        actionRequestOpen={actionRequestOpen}
        setActionRequestOpen={setActionRequestOpen}
        historyOpen={historyOpen}
        setHistoryOpen={setHistoryOpen}
        debitOpen={debitOpen}
        setDebitOpen={setDebitOpen}
        submissionOpen={submissionOpen}
        setSubmissionOpen={setSubmissionOpen}
        viewBatchOpen={viewBatchOpen}
        setViewBatchOpen={setViewBatchOpen}
        cancelClaimOpen={cancelClaimOpen}
        setCancelClaimOpen={setCancelClaimOpen}
        invoiceOpen={invoiceOpen}
        setInvoiceOpen={setInvoiceOpen}
        queryModalOpen={queryModalOpen}
        setQueryModalOpen={setQueryModalOpen}
        invoiceModalOpen={invoiceModalOpen}
        setInvoiceModalOpen={setInvoiceModalOpen}
        resubmitOpen={resubmitOpen}
        setResubmitOpen={setResubmitOpen}
        reconcileOpen={reconcileOpen}
        setReconcileOpen={setReconcileOpen}
        matchOpen={matchOpen}
        setMatchOpen={setMatchOpen}
        writeOffOpen={writeOffOpen}
        setWriteOffOpen={setWriteOffOpen}
        markReadyOpen={markReadyOpen}
        setMarkReadyOpen={setMarkReadyOpen}
        markPaidOpen={markPaidOpen}
        setMarkPaidOpen={setMarkPaidOpen}
        markIncompleteOpen={markIncompleteOpen}
        setMarkIncompleteOpen={setMarkIncompleteOpen}
        markHistoricalOpen={markHistoricalOpen}
        setMarkHistoricalOpen={setMarkHistoricalOpen}
        markUninsuredOpen={markUninsuredOpen}
        setMarkUninsuredOpen={setMarkUninsuredOpen}
        markPendingWriteOffOpen={markPendingWriteOffOpen}
        setMarkPendingWriteOffOpen={setMarkPendingWriteOffOpen}
        internalCommentOpen={internalCommentOpen}
        setInternalCommentOpen={setInternalCommentOpen}
        receiptOpen={receiptOpen}
        setReceiptOpen={setReceiptOpen}
        viewActionsOpen={viewActionsOpen}
        setViewActionsOpen={setViewActionsOpen}
        sourceDataOpen={sourceDataOpen}
        setSourceDataOpen={setSourceDataOpen}
        attachmentsOpen={attachmentsOpen}
        setAttachmentsOpen={setAttachmentsOpen}
        supportingDocumentOpen={supportingDocumentOpen}
        setSupportingDocumentOpen={setSupportingDocumentOpen}
        handleReconcileSelected={handleReconcileSelected}
      />
      <BillingDataGridWrapper
        className={
          !isProvider && !isAdmin ? BILLER_CLAIMS_CLASS_NAMES.page : undefined
        }
        title={
          !isProvider && !isAdmin
            ? translate('pages:claims.billerTitle', 'Claim Management')
            : title
        }
        subtitle={isProvider ? providerName : undefined}
        subtitleColor={isProvider ? PROVIDER_CLAIMS_ACCENT_COLOR : undefined}
        titleAside={
          (isProvider || (!isAdmin && showClaimTotals)) && !isSmallScreen ? (
            <ClaimTotalsPanel
              submitted={claims.totalAmountSubmitted}
              paid={claims.totalAmountPaid}
              outstanding={claims.totalAmountOutstanding}
              formatCurrency={formatCurrency}
              compact
              biller={!isProvider}
              matchingClaims={claims.total}
            />
          ) : null
        }
        tableOptions={tableOptions}
        dataTestID="claimsManagement"
        onTableReady={(table) => setTableInstance(table)}
        reservePatientBannerSpace={isAdmin}
        bannerAside={
          isAdmin && showClaimTotals && !isSmallScreen ? (
            <ClaimTotalsPanel
              submitted={claims.totalAmountSubmitted}
              paid={claims.totalAmountPaid}
              outstanding={claims.totalAmountOutstanding}
              formatCurrency={formatCurrency}
            />
          ) : null
        }
        preTableContent={
          tableInstance ? (
            isProvider ? (
              <ProviderNeedsYou
                count={providerActionRequestCount}
                variant="banner"
              />
            ) : (
              <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
                <ClaimToolBar
                  table={tableInstance as unknown as MRT_TableInstance<Claim>}
                  providers={providers}
                  payors={payors}
                  handleCreateSubmission={handleCreateSubmission}
                  isFetching={isFetching}
                  refetchClaims={refetchClaims}
                  refetchStatusesCount={refetchStatusesCount}
                  setCreateModalType={setCreateModalType}
                  setUseSelectedClaimForDuplicate={
                    setUseSelectedClaimForDuplicate
                  }
                  setManualLoading={setManualLoading}
                />
                <Flex align="center" gap="sm" wrap="wrap">
                  <ClaimsToolbarInternalActions
                    table={tableInstance as unknown as MRT_TableInstance<Claim>}
                    isProvider={isProvider}
                    placement="external"
                  />
                </Flex>
              </Flex>
            )
          ) : null
        }
      />
    </>
  );
};
